import { use, useEffect, useRef } from "react";

import { getConfig } from "lib/get-config";
import { captureException } from "lib/logger";
import { orderByChapter, xpointerToChapter } from "lib/navigation/paths";
import { NavigationContext } from "lib/navigation/use-navigation";
import { usePaths } from "lib/navigation/use-paths";
import { SettingsContext } from "lib/settings/use-settings";

import { getSymbolsOnPage, resetSymbolCount } from "./count-symbols";
import { postTextStats, type StatsItem } from "./send-report";

const SYMBOLS_PER_MINUTE = 7000;
const SEND_INTERVAL_MS = 60000;

export function useStats() {
  const { art, user, trials } = getConfig();
  const { page, totalPages, bookmark } = use(NavigationContext)!;
  const { fontSize, cols } = use(SettingsContext)!;
  const { context } = usePaths();

  const xpointer = bookmark?.xpointer ?? null;

  const statsEventsRef = useRef<StatsItem[]>([]);
  const lastNavigationRef = useRef<{
    xpointer: string | null;
    timestamp: number | null;
  }>({
    xpointer: null,
    timestamp: null,
  });

  // Сброс при изменении параметров страницы
  useEffect(() => {
    resetSymbolCount();
  }, [fontSize, cols, totalPages]);

  async function flushStats() {
    if (statsEventsRef.current.length === 0) {
      return;
    }

    try {
      await postTextStats({
        statistics: statsEventsRef.current,
      });

      statsEventsRef.current = [];
    } catch (error) {
      captureException(error as Error);
    }
  }

  useEffect(() => {
    if (xpointer !== null && page !== null && user) {
      // Инициализация
      if (lastNavigationRef.current.xpointer === null) {
        lastNavigationRef.current = { xpointer, timestamp: Date.now() };
      } else if (lastNavigationRef.current.xpointer !== xpointer) {
        const now = Date.now();
        const timeSinceLastNavigation =
          now - lastNavigationRef.current.timestamp!;

        const symbolsOnPage = getSymbolsOnPage(page, context);

        const expectedReadingTime =
          (symbolsOnPage / SYMBOLS_PER_MINUTE) * 60 * 1000;
        const isFlipping = timeSinceLastNavigation < expectedReadingTime;

        const currentChapter = xpointerToChapter(xpointer, context);
        const chapterOrder = currentChapter
          ? orderByChapter(currentChapter, context.toc)
          : null;

        const event: StatsItem = {
          art_id: art,
          is_trial: trials === 1,
          read_at: new Date().toISOString(),
          percent: Math.round((page / totalPages) * 100),
          scroll_events: Number(isFlipping),
          statistics_received: navigator.onLine ? "online" : "offline",
          symbols_count: isFlipping ? 0 : symbolsOnPage,
          chapter_statistics: [
            { chapter_index: chapterOrder ? chapterOrder - 1 : 0 },
          ],
        };

        statsEventsRef.current.push(event);
        lastNavigationRef.current = { xpointer, timestamp: now };
      }
    }
  }, [xpointer, page, totalPages, user, art, context, trials]);

  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        flushStats();
      }, SEND_INTERVAL_MS);

      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      function handleVisibilityChange() {
        if (document.visibilityState === "hidden") {
          flushStats();
        }
      }

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        flushStats();
      };
    }
  }, [user]);
}
